import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { DynamicTableInterface } from 'src/app/models/interface/dynamicTable';
import { DvirService } from './dvir.service';
import { Dvir } from 'src/app/models/dvir';
import { PagedResponse } from 'src/app/models/pagedResponse';
import { filter, take, tap } from 'rxjs/operators';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { Select } from '@ngxs/store';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { Observable } from 'rxjs';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';

@Component({
    selector: 'app-dvir',
    templateUrl: './dvir.component.html',
    styleUrls: ['./dvir.component.css'],
    standalone: false
})
export class DvirComponent implements OnInit, DynamicTableInterface {
    @Input() tenantId: string;
    @Input() vin: string;
    @Output() public functionCallback = new EventEmitter();
    @Select(TenantSessionState.getTenantFeatures) getTenantFeatures$: Observable<FeatureSettings>;
    @ViewChild('isFalseCleanTemplate') isFalseCleanTemplate;

    loading = true;
    dvirData: Dvir[];
    dataSource = new MatTableDataSource<any>();
    matSortActive = '';
    baseSort = 'dvirCreateDateTimeUtc: -1';
    defectsOnly: boolean = true;
    private currentSort = `{${this.baseSort}}`;

    private currentFilter = '{}';
    private currentOffset = 0;
    private currentLimit = 10;
    includeSelect = true;

    searchString = `{$or: [
        {"notes": /|replaceText|/i},
    ]}`;

    gridDisplayColumns = [
        {
            display: '@dvirCreateDateTimeUtc',
            property: 'dvirCreateDateTimeUtc',
            type: 'date',
        },
        {
            display: '@logType',
            property: 'logType',
            type: 'text',
        },
        {
            display: '@notes',
            property: 'notes',
            type: 'text',
        },
        {
            display: '@defectCount',
            property: 'defectCount',
            type: 'number',
            noSort: true,
        },
        {
            display: '@isSafeToOperate',
            property: 'isSafeToOperate',
            type: 'booleanx',
        },
        {
            display: '@repairDateTimeUtc',
            property: 'repairDateTimeUtc',
            type: 'date',
            hidden: true,
        }
      ];
      tableFunctions = [
        {
          display: '@showAllColumns',
          action: 'show',
          icon: 'fa fa-eye'
        }
      ];
      length: number;

    constructor(
        private dvirService: DvirService,
        private router: Router,
        private spaRoutingService: SpaRoutingService) {

    }

    ngOnInit(): void {
      this.getTenantFeatures$.pipe(filter(f => f != null), take(1)).subscribe(f => {
        if (isFeatureEnabled(f, TenantFeatures.CheckDVIRFalseClean)) {
          this.gridDisplayColumns.push({
            display: '@isFalseClean',
            property: 'isFalseCleanTemplate',
            type: 'template',
            noSort: true
          })
        }
      })
    }

    toggleDefectsOnly($event: MatCheckboxChange): void {
      this.filterChanged(this.currentFilter);
    }

    getData(offset: number, limit: number, filter: string, sort: string): void {
        this.loading = true;
        const dvirData$ = this.dvirService.getDvirByVin(
            this.vin,
            offset,
            limit,
            filter,
            sort,
            this.defectsOnly)
            .pipe(tap((rsp: PagedResponse) => {
                rsp.documents.forEach(dvir => {
                    dvir.defectCount = dvir.defectList?.length ?? 0;

                    const falseCleanTemplate = {
                          template: this.isFalseCleanTemplate,
                          inputs: {data: {isFalseClean: dvir.isFalseClean, reason: dvir.falseCleanReason}}
                        };
                    dvir.isFalseCleanTemplate = falseCleanTemplate
                });
                this.dataSource = new MatTableDataSource(rsp.documents);
                this.length = rsp.count;
                this.loading = false;
            }));
        dvirData$.subscribe();
    }

    pageChangedEvent(event): void {
        const pageIndex = event.pageIndex;
        const pageSize = event.pageSize;
        this.currentLimit = pageSize;
        const previousSize = pageSize * pageIndex;
        this.currentOffset = previousSize;
        this.getData(
          this.currentOffset,
          this.currentLimit,
          this.currentFilter,
          this.currentSort
        );
      }

      filterChanged(filterList: string): void {
        this.currentFilter = filterList;
        this.currentOffset = 0;
    
        this.getData(
          this.currentOffset,
          this.currentLimit,
          this.currentFilter,
          this.currentSort);
      }
    
      sortDataEvent(sort: string): void {
        this.currentSort = sort;
        this.currentOffset = 0;
        this.getData(
          this.currentOffset,
          this.currentLimit,
          this.currentFilter,
          this.currentSort);
      }

      selectRowEvent(event: any): void {
        this.dvirService.selectedDvir = event.row;
        this.spaRoutingService.navigateToDvir(event.row.id);
      }

      refreshDataEvent(event: any): void {
        const pageIndex = event.pageIndex;
        const pageSize = event.pageSize;
        this.currentLimit = pageSize;
        const previousSize = pageSize * pageIndex;
        this.currentOffset = previousSize;
        this.currentFilter = event.filter;
        this.currentSort = event.sortString;
        this.getData(
          this.currentOffset,
          this.currentLimit,
          this.currentFilter,
          this.currentSort
        );
    
        this.toggleShowColumns(event.showAllColumns);
    
      }
    
      toggleShowColumns(showAllColumn: boolean): void {
        this.gridDisplayColumns.forEach(col => {
          if (showAllColumn) {
            if (col.hidden === true) {
              col.hidden = false;
            }
          }
          else {
            if (col.hidden === false) {
              col.hidden = true;
            }
          }
        });
      }

      processFunction(event: any): void {
        switch (event.action) {
          case 'navigate':
            this.selectRowEvent(event);
            break;
          case 'page':
            this.pageChangedEvent(event);
            break;
          case 'filter':
            this.filterChanged(event.filter);
            break;
          case 'sort':
            this.sortDataEvent(event.sort);
            break;
          case 'refresh':
            this.refreshDataEvent(event);
            break;
          case 'show':
            this.toggleShowColumns(event.showAllColumns);
            break;
          case 'add':
            //this.router.navigate(['fleet', 'vehicle', this.tenantId, this.vin, 'maintenance']);
            break;
        }
      }
}